import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { useAuthStore } from '@/app/store/authStore';
import { ApiError } from '@/services/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { changeLanguage, currentLanguage, LANGUAGES } from '@/i18n';
import { cn } from '@/lib/utils';

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const login = useAuthStore((state) => state.login);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const user = await login(username.trim(), password);

      // A credential the admin set cannot be used for real work until changed.
      if (user.mustChangePassword) {
        navigate('/change-password', { replace: true });
        return;
      }

      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? '/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        // Map the server's code to a translated message rather than showing
        // the English text a Sinhala user would not expect.
        const messages: Record<string, string> = {
          INVALID_CREDENTIALS: t('auth.invalidCredentials'),
          ACCOUNT_DISABLED: t('auth.accountDisabled'),
          NETWORK_ERROR: t('errors.network'),
          TOO_MANY_ATTEMPTS: caught.message,
        };
        setError(messages[caught.code] ?? caught.message);
      } else {
        setError(t('errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <img
            src="/tharanga-logo.png"
            alt={t('app.name')}
            className="mx-auto h-14 w-14 rounded-2xl object-contain"
          />
          <h1 className="mt-4 text-xl font-semibold text-slate-900">{t('app.name')}</h1>
          <p className="text-sm text-slate-500">{t('app.subtitle')}</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <p className="text-sm text-slate-600">{t('auth.welcome')}</p>

          {error && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <Input
            label={t('auth.username')}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
          />

          <Input
            label={t('auth.password')}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={isSubmitting}
            disabled={!username.trim() || !password}
          >
            {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>

        {/* Language choice must be reachable before signing in — an agent who
            only reads Sinhala should not have to navigate an English login. */}
        <div className="mt-4 flex items-center justify-center gap-2">
          <Globe className="h-4 w-4 text-slate-400" />
          {LANGUAGES.map((language) => (
            <button
              key={language.code}
              type="button"
              onClick={() => changeLanguage(language.code)}
              className={cn(
                'rounded-md px-2 py-1 text-sm',
                language.code === currentLanguage()
                  ? 'font-medium text-brand-700'
                  : 'text-slate-500 hover:text-slate-700',
              )}
            >
              {language.nativeLabel}
            </button>
          ))}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">© Adeona Technologies. All Rights Reserved</p>
      </div>
    </div>
  );
}
